// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { screenerApi } from '@shared/data-access/api/ai/screener-api';
import { type StocksSummaryResp } from '@shared/data-access/interfaces/api/ai/screener';
import { generatePayloadCustomUniverse } from '@advisor-app/features/utils/generate-payload-custom-universe';
import { useSafeUpdate } from '@shared/ui-kit/hooks';
import { useHandleApiErrors } from '@advisor-app/hooks/use-handle-api-errors';
import { useEffect, useState } from 'react';
import { useAppSelector } from '@advisor-app/store/store';
import {
  selectObjectiveKey,
  selectUniverseFilters,
} from '@advisor-app/store/slices/parameters';
import { selectWorkflowAsset } from '@advisor-app/store/slices/workflow';

const initialState = {
  dividend_yield: 0,
  market_cap: 0,
  number_stocks: 0,
  pb: 0,
  pe: 0,
};

export const useGetCustomUniverse = () => {
  const handleApiErrors = useHandleApiErrors();
  const runSafeUpdate = useSafeUpdate();
  const assetType = useAppSelector(selectWorkflowAsset);
  const objective = useAppSelector(state =>
    selectObjectiveKey(state, 'ai-builder'),
  );
  const universe = useAppSelector(state =>
    selectUniverseFilters(state, 'ai-builder'),
  )!;
  const {
    factors_filter,
    sectors_filter,
    esg_exclusions,
    esg_scoring,
    esg_not_informed,
    to_exclude,
    to_include,
    universe_base,
  } = universe;

  const [triggerGetStockSummary] = screenerApi.useLazyGetStockSummaryQuery();
  const [customUniverse, setCustomUniverse] =
    useState<StocksSummaryResp>(initialState);

  useEffect(() => {
    const getUniverseDetail = async () => {
      try {
        const payload = generatePayloadCustomUniverse(universe);
        const summaryResult = await triggerGetStockSummary(payload).unwrap();
        runSafeUpdate(() => setCustomUniverse(summaryResult));
      } catch (error: any) {
        handleApiErrors(error);
      }
    };
    if (assetType === 'stock' && objective === 'min_risk_parity') {
      getUniverseDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sectors_filter,
    factors_filter,
    esg_exclusions,
    esg_scoring,
    esg_not_informed,
    universe_base,
    to_include,
    to_exclude,
    assetType,
    objective,
    triggerGetStockSummary,
    runSafeUpdate,
  ]);

  return customUniverse;
};
